import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { SalesTaxTargetAccountType } from '@releve/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumberInput } from '@/components/ui/number-input';
import {
  StandardDialog,
  StandardDialogBody,
  StandardDialogContent,
  StandardDialogFooter,
  StandardDialogHeader,
  StandardDialogTitle,
} from '@/components/ui/standard-dialog';
import { useTranslation } from '@/hooks/useTranslation';
import { useCreateCustomTaxRate } from '@/hooks/useOrganizationTaxSettings';
import type { TaxDirection } from '@/hooks/useJurisdictionTaxConfig';
import {
  DEFAULT_SALES_TAX_ACCOUNT,
  SalesTaxAccountSelect,
  salesTaxTargetToSend,
  useSalesTaxTargetAccounts,
} from '@/components/tax/SalesTaxAccountSelect';

interface LineItemCustomRateDialogProps {
  onOpenChange: (open: boolean) => void;
  organizationId: string | undefined;
  direction: TaxDirection;
  taxSystem: 'VAT' | 'SALES_TAX';
  /** Called with the new rate's id and percentage once it is created. */
  onCreated: (rateId: string, rate: number) => void;
}

/**
 * The line-item "Custom rate…" dialog of InvoiceLineItems: creates a custom
 * rate and hands it back so the line picks it. For a sales-tax org it offers
 * the same payable-account choice as Settings → Tax (SalesTaxAccountSelect),
 * so a custom meals / rooms-type rate made from a line can post to Meals &
 * Rooms Tax Payable too.
 *
 * Mount it conditionally (`{open && <LineItemCustomRateDialog … />}`): it
 * reads the chart of accounts, and every open starts with a fresh form.
 */
export function LineItemCustomRateDialog({
  onOpenChange,
  organizationId,
  direction,
  taxSystem,
  onCreated,
}: LineItemCustomRateDialogProps) {
  const { t } = useTranslation('common');
  const createCustomRate = useCreateCustomTaxRate(organizationId);
  const [name, setName] = useState('');
  const [value, setValue] = useState<number | string>(0);
  const [account, setAccount] = useState<SalesTaxTargetAccountType>(DEFAULT_SALES_TAX_ACCOUNT);
  const accountOptions = useSalesTaxTargetAccounts(organizationId, taxSystem === 'SALES_TAX');

  const numRate = typeof value === 'string' ? parseFloat(value) || 0 : value;

  const submit = () => {
    const targetAccountType = salesTaxTargetToSend(accountOptions, account);
    createCustomRate.mutate(
      {
        name,
        rate: numRate,
        direction,
        taxType: taxSystem,
        ...(targetAccountType ? { targetAccountType } : {}),
      },
      {
        onSuccess: (newRateId) => {
          onCreated(newRateId, numRate);
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <StandardDialog open onOpenChange={onOpenChange}>
      <StandardDialogContent size="sm">
        <StandardDialogHeader>
          <StandardDialogTitle>{t('tax.settings.customRates.dialogTitle')}</StandardDialogTitle>
        </StandardDialogHeader>
        <StandardDialogBody className="space-y-4">
          <div>
            <Label>{t('tax.settings.customRates.rateLabel')}</Label>
            <NumberInput
              value={value}
              onChange={(val) => {
                setValue(val);
                const numVal = typeof val === 'string' ? parseFloat(val) || 0 : val;
                setName(t('tax.settings.customRates.defaultName', { rate: numVal }));
              }}
              placeholder="0.00"
              step={0.01}
              min={0}
              max={100}
            />
          </div>
          <div>
            <Label>{t('tax.ui.settings.columns.name')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('tax.settings.customRates.namePlaceholder')} />
          </div>
          <SalesTaxAccountSelect options={accountOptions} value={account} onChange={setAccount} />
          <p className="text-xs text-muted-foreground">
            {direction === 'OUTPUT'
              ? t('tax.settings.customRates.lineSavedAsSales')
              : t('tax.settings.customRates.lineSavedAsPurchase')}
          </p>
        </StandardDialogBody>
        <StandardDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cancel')}
          </Button>
          <Button disabled={createCustomRate.isPending || !name || numRate <= 0} onClick={submit}>
            {createCustomRate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {createCustomRate.isPending
              ? t('tax.settings.customRates.adding')
              : t('tax.settings.customRates.addRate')}
          </Button>
        </StandardDialogFooter>
      </StandardDialogContent>
    </StandardDialog>
  );
}
